import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link, useLocation } from "react-router-dom";
import { fetchHubEntry } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import KnowledgeCard from "@/components/KnowledgeCard";
import RichText from "@/components/RichText";
import RelatedServices from "@/components/RelatedServices";
import PageEngage from "@/components/PageEngage";
import { Divider } from "@/components/MotifBackground";
import { ChevronRight } from "lucide-react";

const SECTION_PATH = { scriptures: "/scriptures", rishis: "/rishis", concepts: "/philosophy", traditions: "/traditions", pilgrimage: "/pilgrimage", samskara: "/samskaras", sanskrit: "/sanskrit", explainers: "/explained", vrata: "/vrata" };

export default function HubDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { t, lang, pick } = useLang();
  const [e, setE] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setE(null); setNotFound(false);
    fetchHubEntry(slug).then(setE).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3">{t("entry_not_found")}</div>;
  if (!e) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3">…</div>;

  return (
    <article className="relative pb-24" data-testid="hub-detail">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <nav className="flex items-center gap-1.5 pt-16 text-xs text-ink-3 md:pt-24">
          <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={SECTION_PATH[e.section] || "/"} className="hover:text-saffron">{e.section_meta?.title}</Link>
        </nav>
        <p className="mt-6 overline-label text-gold">{e.section_meta?.title}</p>
        <h1 className="mt-2 font-deva text-5xl leading-tight text-ink deva-lh md:text-6xl">{e.deva_name}</h1>
        <p className="mt-2 font-serif-latin text-2xl italic text-ink-2">{pick(e.title_hi, e.title)}</p>
        <p className="mt-1 text-sm text-gold">{pick(e.subtitle_hi, e.subtitle)}</p>

        {pick(e.detail_heading_hi, e.detail_heading) && (
          <p className="mt-8 overline-label text-saffron" data-testid="hub-detail-heading">{pick(e.detail_heading_hi, e.detail_heading)}</p>
        )}
        <RichText value={pick(e.summary_hi, e.summary)} className={`${pick(e.detail_heading_hi, e.detail_heading) ? "mt-2" : "mt-8"} max-w-[62ch] font-serif-latin text-xl leading-relaxed text-ink`} />
        <RichText value={pick(e.body_hi, e.body)} className="mt-6 max-w-[65ch] font-serif-latin text-[17px] font-medium leading-[1.9] text-ink" />

        {(() => {
          const pts = lang === "hi" && e.points_hi?.length ? e.points_hi : e.points;
          return pts?.length > 0 && (
            <div className="mt-10 space-y-4">
              {pts.map((p, i) => (
                <div key={i} className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-4">
                  <p className="font-deva-body text-lg text-ink">{p.head}</p>
                  <p className="mt-1 text-[15px] leading-relaxed text-ink-2">{p.text}</p>
                </div>
              ))}
            </div>
          );
        })()}

        {e.attributed?.length > 0 && (
          <section className="mt-12">
            <p className="overline-label mb-4 text-ink-3">{t("texts_attributed")}</p>
            <ul className="grid gap-2.5 sm:grid-cols-2">
              {e.attributed.map((a, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[16px] text-ink-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[var(--gold)]" />{a}
                </li>
              ))}
            </ul>
          </section>
        )}

        <RelatedServices section={e.section} />
      </div>
      <PageEngage refType="hub" refSlug={slug} refTitle={pick(e.title_hi, e.title)} />
      {e.related_items?.length > 0 && (
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Divider className="mb-10" />
          <h3 className="mb-8 font-serif-latin text-2xl text-ink">{t("related_on")}</h3>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {e.related_items.map((r) => <KnowledgeCard key={r.slug} item={r} />)}
          </div>
        </div>
      )}
    </article>
  );
}
